
import React, { useState } from "react";
import { useUploads } from "../context/UploadContext";
import {
  FiUploadCloud,
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiXCircle,
  FiX,
  FiTrash2,
  FiLoader,
} from "react-icons/fi";

const BackgroundUploads = () => {
  const { uploads, cancelUpload, removeUpload } = useUploads();
  const [minimized, setMinimized] = useState(false);

  if (uploads.length === 0) return null;

  const activeStatuses = ["preparing", "compressing", "uploading", "cancelling"];
  const activeCount = uploads.filter((upload) => activeStatuses.includes(upload.status)).length;

  const getStatus = (upload) => {
    switch (upload.status) {
      case "preparing": return "Preparing videos...";
      case "compressing": return "Compressing videos...";
      case "uploading": return upload.progress >= 99 ? "Processing post..." : "Uploading...";
      case "completed": return "Completed";
      case "failed": return "Failed";
      case "cancelled": return "Cancelled";
      case "cancelling": return "Cancelling...";
      case "processing": return "Publication already started";
      default: return "Waiting...";
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[9999] w-[calc(100vw-2.5rem)] max-w-[380px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <FiUploadCloud size={22} />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900">Background uploads</h3>
            <p className="text-xs text-gray-500">
              {activeCount > 0 ? `${activeCount} upload(s) in progress` : "All uploads finished"}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setMinimized((prev) => !prev)}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          aria-label={minimized ? "Expand uploads" : "Minimize uploads"}
        >
          {minimized ? <FiChevronUp size={19} /> : <FiChevronDown size={19} />}
        </button>
      </div>

      {!minimized && (
        <div className="max-h-[350px] space-y-4 overflow-y-auto p-4">
          {uploads.map((upload) => {
            const isActive = activeStatuses.includes(upload.status);
            const isCompleted = upload.status === "completed";
            const isFailed = upload.status === "failed";
            const isCancelled = upload.status === "cancelled";
            const canCancel = ["preparing", "compressing", "uploading"].includes(upload.status);
            const progress = Math.max(0, Math.min(100, Number(upload.progress) || 0));

            return (
              <div key={upload.id} className="space-y-2">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                    {isCompleted ? (
                      <FiCheckCircle className="text-green-500" size={20} />
                    ) : isFailed ? (
                      <FiXCircle className="text-red-500" size={20} />
                    ) : isActive ? (
                      <FiLoader className="animate-spin text-blue-500" size={20} />
                    ) : (
                      <FiXCircle className="text-gray-400" size={20} />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{upload.name}</p>
                    <p className={`text-xs ${isFailed ? "text-red-500" : isCompleted ? "text-green-600" : isCancelled ? "text-gray-400" : "text-gray-500"}`}>
                      {getStatus(upload)}
                    </p>

                    {upload.error && <p className="mt-1 text-xs text-red-500">{upload.error}</p>}
                  </div>

                  {canCancel ? (
                    <button
                      type="button"
                      onClick={() => cancelUpload(upload.id)}
                      className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                      title="Cancel upload"
                    >
                      <FiX size={18} />
                    </button>
                  ) : isActive ? (
                    <button type="button" disabled className="rounded-lg p-1 text-gray-400 opacity-40" title="Cancelling">
                      <FiX size={18} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeUpload(upload.id)}
                      className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                      title="Remove from list"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>

                {isActive && (
                  <div className="space-y-1">
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-right text-xs text-gray-500">{progress}%</p>
                  </div>
                )}

                <div className="border-b border-gray-100" />
              </div>
            );
          })}

          <p className="text-center text-xs text-gray-400">
            You can keep browsing while uploads continue.
          </p>
        </div>
      )}
    </div>
  );
};

export default BackgroundUploads;
